import { MouseEventHandler } from 'react';

import { getBalance } from '@wagmi/core';
import toast from 'react-hot-toast';
import { useAccount } from 'wagmi';

import { updateLoanMemberAddress } from '@/api/loans';
import useGasPrice from '@/hooks/query/useGasPrice';
import { checkERC20Balance } from '@/utils/checkERC20Balance';
import { convertValueToWei } from '@/utils/convertValueToWei';
import { getContractDecimals } from '@/utils/getContractDecimals';
import { cn } from '@/utils/helper';
import { config } from 'config/wagmi-config';
import { vaultAddress } from 'smart-contracts/addresses';
import { useErc20Contract, useNettyContract } from 'smart-contracts/contracts';
import { useRouter } from 'next/router';

interface IRepayLoanProps {
  className?: string;
  loanChainId: string;
  currency: string;
  repayNative: string;
  loanId: string | number;
}

function RepayLoan({ className, loanChainId, currency, repayNative, loanId: id }: IRepayLoanProps) {
  const { address } = useAccount();
  const generateErc20Contract = useErc20Contract();
  const nettyContract = useNettyContract();
  const { data: gasPriceInGwei } = useGasPrice();
  const router = useRouter();

  const repayLoan: MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.stopPropagation();
    if (!address) return;

    const loadId = toast.loading('Please wait...');
    try {
      const erc20Contract = generateErc20Contract(currency);
      if (!erc20Contract || !id) return;
      const decimals = await getContractDecimals(erc20Contract);
      const repay = convertValueToWei(repayNative, decimals, true);

      if (!(await checkERC20Balance(erc20Contract, address, repay))) return;

      const [allowFunds] = await erc20Contract.functions.allowance(address, vaultAddress);

      toast.dismiss(loadId);
      if (allowFunds.lt(repay)) {
        await toast.promise(
          (async () => {
            try {
              const waiting = await erc20Contract.functions.approve(vaultAddress, repay);
              await waiting.wait();
            } catch (e) {
              if ((e as { code: string }).code === 'ACTION_REJECTED') {
                const balance = await getBalance(config, {
                  address,
                });

                const weiValueBalance = balance.value;

                const gasPriceInWei = Number(gasPriceInGwei.suggestBaseFee) * 1000000000;

                if (weiValueBalance < gasPriceInWei) {
                  throw new Error('Offer Canceled. Please ensure you have enough ETH to cover gas fees.');
                }
                throw new Error('Offer Canceled');
              }
            }
          })(),
          {
            error: (e) => {
              return e.message || 'Something went wrong';
            },
            loading: 'Please wait until the repayment amount is approved.',
            success: 'The repayment amount has been successfully approved.',
          }
        );
      }

      await toast.promise(
        (async () => {
          try {
            await updateLoanMemberAddress({ id, address: 'lenderAddress' });

            const paybacked = await nettyContract.functions.payBackLoan(loanChainId, currency);

            const data = await paybacked.wait();
            return data;
          } catch (e) {
            const { reason } = e as { reason: string };

            if (reason.includes('caller is not borrower')) {
              throw new Error('Loan can’t be repaid without Obligation Receipt.');
            }

            if ((e as { code: string }).code === 'ACTION_REJECTED') {
              const balance = await getBalance(config, {
                address,
              });

              const weiValueBalance = balance.value;

              const gasPriceInWei = Number(gasPriceInGwei.suggestBaseFee) * 1000000000;

              if (weiValueBalance < gasPriceInWei) {
                throw new Error('Offer Canceled. Please ensure you have enough ETH to cover gas fees.');
              }
              throw new Error('Offer Canceled');
            }

            throw new Error(JSON.stringify(e));
          }
        })(),
        {
          error: (e) => {
            return e.message;
          },
          loading: 'Please wait until the loan is repaid.',
          success: 'The loan has been successfully repaid.',
        }
      );
    } finally {
      toast.dismiss(loadId);
      router.push('/loans/active');
    }
  };

  return (
    <button onClick={repayLoan} className={cn('bg-primary rounded font-semibold text-sm lg:text-base text-white flex items-center justify-center w-[106px] h-11', className)}>
      Repay
    </button>
  );
}
export default RepayLoan;
